import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Upload, Link2, X } from 'lucide-react';
import { getReleases, getArtists, createRelease, updateRelease, deleteRelease, uploadFile, getImageUrl } from '../../utils/api';
import { toast } from 'sonner';
import './AdminStyles.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_OPTIONS = [
  'Spotify', 'Apple Music', 'Amazon Music', 'Deezer', 'YouTube', 'YouTube Music',
  'Beatport', 'Traxsource', 'SoundCloud', 'TIDAL', 'Bandcamp', 'Instagram', 'TikTok'
];

const ReleaseManagement = () => {
  const [releases, setReleases] = useState([]);
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRelease, setEditingRelease] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    artist_id: '',
    artist_name: '',
    artwork: '',
    stream_link: '',
    genre: '',
    release_date: ''
  });
  const [uploading, setUploading] = useState(false);
  const [linksModal, setLinksModal] = useState(null); // release object
  const [editLinks, setEditLinks] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [releasesData, artistsData] = await Promise.all([
        getReleases(),
        getArtists()
      ]);
      setReleases(releasesData);
      setArtists(artistsData);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await uploadFile(file);
      setFormData({ ...formData, artwork: result.url });
      toast.success('Artwork uploaded');
    } catch (error) {
      console.error('Upload error:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
      } else {
        toast.error('Failed to upload artwork. Please try again.');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleArtistChange = (e) => {
    const artistId = e.target.value;
    const selectedArtist = artists.find(a => a.id === artistId);
    setFormData({
      ...formData,
      artist_id: artistId,
      artist_name: selectedArtist ? selectedArtist.name : ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRelease) {
        await updateRelease(editingRelease.id, formData);
        toast.success('Release updated');
      } else {
        await createRelease(formData);
        toast.success('Release created');
      }
      fetchData();
      closeModal();
    } catch (error) {
      toast.error('Failed to save release');
    }
  };

  const handleEdit = (release) => {
    setEditingRelease(release);
    setFormData({
      title: release.title,
      artist_id: release.artist_id,
      artist_name: release.artist_name,
      artwork: release.artwork || '',
      stream_link: release.stream_link,
      genre: release.genre,
      release_date: release.release_date || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (releaseId) => {
    if (!window.confirm('Are you sure you want to delete this release?')) return;
    
    try {
      await deleteRelease(releaseId);
      toast.success('Release deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete release');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingRelease(null);
    setFormData({
      title: '',
      artist_id: '',
      artist_name: '',
      artwork: '',
      stream_link: '',
      genre: '',
      release_date: ''
    });
  };

  const openLinksEditor = async (release) => {
    try {
      const res = await fetch(`${API_URL}/api/releases/${release.id}/links`);
      const data = await res.json();
      setEditLinks(data.links || []);
    } catch { setEditLinks([]); }
    setLinksModal(release);
  };

  const addLink = () => {
    setEditLinks(prev => [...prev, { name: '', url: '' }]);
  };

  const removeLink = (idx) => {
    setEditLinks(prev => prev.filter((_, i) => i !== idx));
  };

  const updateLink = (idx, field, value) => {
    setEditLinks(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  };

  const saveLinks = async () => {
    const token = localStorage.getItem('adminToken');
    const validLinks = editLinks.filter(l => l.name && l.url);
    try {
      const res = await fetch(`${API_URL}/api/admin/releases/${linksModal.id}/links`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ links: validLinks })
      });
      if (!res.ok) throw new Error('Failed to save');
      toast.success('Streaming links updated');
      setLinksModal(null);
    } catch (err) { toast.error(err.message); }
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Release Management</h1>
        <button className="btn btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={20} />
          Add Release
        </button>
      </div>

      <div className="admin-table-container">
        {releases.length === 0 && (
          <div style={{padding: 'var(--spacing-xl)', textAlign: 'center', color: 'var(--light-grey)'}}>
            No releases yet. Click "Add Release" to create one.
          </div>
        )}
        {releases.length > 0 && (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Artwork</th>
                <th>Title</th>
                <th>Artist</th>
                <th>Genre</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {releases.map((release) => {
                return (
                  <tr key={release.id}>
                    <td>
                      {release.artwork && (
                        <img src={getImageUrl(release.artwork)} alt={release.title} style={{width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px'}} />
                      )}
                    </td>
                    <td>{release.title}</td>
                    <td>{release.artist_name}</td>
                    <td>{release.genre}</td>
                    <td>
                      <div className="table-actions">
                        <button className="action-btn" onClick={() => openLinksEditor(release)} data-testid={`links-btn-${release.id}`}>
                          <Link2 size={16} /> Links
                        </button>
                        <button className="action-btn" onClick={() => handleEdit(release)}>
                          <Edit size={16} /> Edit
                        </button>
                        <button className="action-btn delete" onClick={() => handleDelete(release.id)}>
                          <Trash2 size={16} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{editingRelease ? 'Edit Release' : 'Add Release'}</h2>
              <button className="close-btn" onClick={closeModal} type="button">×</button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Track Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Artist *</label>
                  <select
                    value={formData.artist_id}
                    onChange={handleArtistChange}
                    required
                  >
                    <option value="">Select Artist</option>
                    {artists.map((artist) => (
                      <option key={artist.id} value={artist.id}>
                        {artist.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Genre *</label>
                  <input
                    type="text"
                    value={formData.genre}
                    onChange={(e) => setFormData({ ...formData, genre: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Release Date</label>
                  <input
                    type="date"
                    value={formData.release_date}
                    onChange={(e) => setFormData({ ...formData, release_date: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Primary Link (Songstats, Spotify, etc.)</label>
                <input
                  type="url"
                  placeholder="https://open.spotify.com/..."
                  value={formData.stream_link}
                  onChange={(e) => setFormData({ ...formData, stream_link: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Artwork</label>
                <span className="dimension-hint">Recommended: 1400 x 1400px (square)</span>
                <div className="file-upload-area">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    id="release-artwork"
                    style={{display: 'none'}}
                  />
                  <label htmlFor="release-artwork" className="upload-btn">
                    <Upload size={20} />
                    {uploading ? 'Uploading...' : 'Upload Artwork'}
                  </label>
                  <input
                    type="text"
                    placeholder="Or paste artwork URL"
                    value={formData.artwork}
                    onChange={(e) => setFormData({ ...formData, artwork: e.target.value })}
                    style={{marginTop: '0.5rem'}}
                  />
                  {formData.artwork && (
                    <img src={getImageUrl(formData.artwork)} alt="Preview" className="image-preview" />
                  )}
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingRelease ? 'Update Release' : 'Create Release'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Streaming Links Editor Modal */}
      {linksModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: 640 }} data-testid="links-editor-modal">
            <div className="modal-header">
              <h2>Streaming Links — {linksModal.title}</h2>
              <button className="close-btn" onClick={() => setLinksModal(null)} type="button">×</button>
            </div>
            <div className="modal-form">
              <p style={{ fontSize: '0.82rem', color: 'var(--light-grey)', marginBottom: 16 }}>
                Add streaming platform links for this release. These appear on the public release page.
              </p>
              {editLinks.map((link, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                  <select value={link.name} onChange={e => updateLink(idx, 'name', e.target.value)}
                    style={{ width: 150, flexShrink: 0 }} data-testid={`link-name-${idx}`}>
                    <option value="">Platform...</option>
                    {PLATFORM_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <input value={link.url} onChange={e => updateLink(idx, 'url', e.target.value)}
                    placeholder="https://..." style={{ flex: 1 }} data-testid={`link-url-${idx}`} />
                  <button type="button" onClick={() => removeLink(idx)}
                    style={{ background: 'none', border: 'none', color: '#ff4444', cursor: 'pointer', padding: 4 }}>
                    <X size={16} />
                  </button>
                </div>
              ))}
              <button type="button" onClick={addLink} className="btn btn-secondary" style={{ marginTop: 8, fontSize: '0.82rem' }}
                data-testid="add-platform-link-btn">
                <Plus size={14} /> Add Platform
              </button>
              <div className="modal-actions" style={{ marginTop: 16 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setLinksModal(null)}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={saveLinks} data-testid="save-links-btn">Save Links</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReleaseManagement;
